import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Card,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  CircularProgress,
  Divider,
  useTheme,
} from '@mui/material';
import {
  Save as SaveIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import MediaUploader from './MediaUploader';
import {
  createGalleryItem,
  updateGalleryItem,
  uploadMedia,
  fetchGalleryItem,
  fetchCategories,
  GalleryCategory,
  CreateGalleryItemData,
} from '../api/galleryApi';
import useAuthStore from '../../../store/authStore';
import { brand } from '../../../config/brand';

const GallerySubmitForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const { user } = useAuthStore();
  const isEdit = Boolean(id);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState('');
  const [instanceUrl, setInstanceUrl] = useState('');
  const [repositoryUrl, setRepositoryUrl] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const DEFAULT_CATEGORIES: GalleryCategory[] = [
    { _id: 'web-app', name: 'Web Application', slug: 'web-app', count: 0 },
    { _id: 'mobile-app', name: 'Mobile Application', slug: 'mobile-app', count: 0 },
    { _id: 'api', name: 'API / Backend', slug: 'api', count: 0 },
    { _id: 'dashboard', name: 'Dashboard / Admin Panel', slug: 'dashboard', count: 0 },
    { _id: 'ecommerce', name: 'E-Commerce / Marketplace', slug: 'ecommerce', count: 0 },
    { _id: 'saas', name: 'SaaS Platform', slug: 'saas', count: 0 },
    { _id: 'landing', name: 'Landing Page / Portfolio', slug: 'landing', count: 0 },
    { _id: 'tool', name: 'Developer Tool / Utility', slug: 'tool', count: 0 },
    { _id: 'cli-tool', name: 'CLI Tool', slug: 'cli-tool', count: 0 },
    { _id: 'extension', name: 'Extension / Plugin', slug: 'extension', count: 0 },
    { _id: 'integration', name: 'Integration', slug: 'integration', count: 0 },
    { _id: 'template', name: 'Template / Boilerplate', slug: 'template', count: 0 },
    { _id: 'ai-ml', name: 'AI / Machine Learning', slug: 'ai-ml', count: 0 },
    { _id: 'game', name: 'Game', slug: 'game', count: 0 },
    { _id: 'social', name: 'Social / Community', slug: 'social', count: 0 },
    { _id: 'other', name: 'Other', slug: 'other', count: 0 },
  ];

  const [categories, setCategories] = useState<GalleryCategory[]>(DEFAULT_CATEGORIES);

  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(isEdit);
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const SUGGESTED_TAGS = [
    'React', 'Vue', 'Angular', 'Node.js', 'TypeScript', 'JavaScript',
    'Python', 'Full-Stack', 'REST API', 'GraphQL', 'Database',
    'Authentication', 'Real-Time', 'Responsive', 'PWA', 'Serverless',
    'Docker', 'CI/CD', 'Material UI', 'Tailwind CSS',
  ];

  useEffect(() => {
    fetchCategories()
      .then((cats) => { if (cats.length > 0) setCategories(cats); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!id) return;
    setPageLoading(true);
    fetchGalleryItem(id)
      .then((item) => {
        setTitle(item.title);
        setDescription(item.description);
        setCategory(item.category);
        setTags(item.tags || []);
        setInstanceUrl(item.instanceUrl || '');
        setRepositoryUrl(item.repositoryUrl || '');
      })
      .catch(() => navigate('/gallery'))
      .finally(() => setPageLoading(false));
  }, [id]);

  const handleAddTag = () => {
    const tag = tagInput.trim().toLowerCase();
    if (tag && !tags.includes(tag) && tags.length < 10) {
      setTags([...tags, tag]);
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setTags(tags.filter((t) => t !== tagToRemove));
  };

  const handleTagKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      handleAddTag();
    }
  };

  const handleSubmit = async () => {
    if (!title.trim()) {
      setError('Title is required.');
      return;
    }
    if (!description.trim()) {
      setError('Description is required.');
      return;
    }
    if (!category) {
      setError('Please select a category.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const data: CreateGalleryItemData = {
        title: title.trim(),
        description: description.trim(),
        category,
        tags,
        ...(instanceUrl.trim() && { instanceUrl: instanceUrl.trim() }),
        ...(repositoryUrl.trim() && { repositoryUrl: repositoryUrl.trim() }),
        // Attach author info from auth store for new submissions
        ...(!isEdit && user?.firstName && {
          authorName: `${user.firstName} ${user.lastName || ''}`.trim(),
        }),
        ...(!isEdit && user?.profilePictureUrl && {
          authorAvatarUrl: user.profilePictureUrl,
        }),
      };

      let itemId: string;

      if (isEdit && id) {
        await updateGalleryItem(id, data);
        itemId = id;
      } else {
        const created = await createGalleryItem(data);
        itemId = created._id;
      }

      // Upload media if there are new files
      if (files.length > 0) {
        setUploading(true);
        setUploadProgress(0);

        // Simulate progress since we can't track multipart easily
        const progressInterval = setInterval(() => {
          setUploadProgress((prev) => Math.min(prev + 10, 90));
        }, 500);

        try {
          await uploadMedia(itemId, files);
          setUploadProgress(100);
        } finally {
          clearInterval(progressInterval);
          setUploading(false);
        }
      }

      navigate(`/gallery/${itemId}`);
    } catch (err: any) {
      setError(
        err.response?.data?.message || 'Failed to save. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  if (pageLoading) {
    return (
      <Container maxWidth="md" sx={{ py: 8, textAlign: 'center' }}>
        <CircularProgress sx={{ color: brand.colors.fire }} />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 4, md: 8 } }}>
      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: 6 }}>
        <Typography variant="h3" fontWeight="bold" gutterBottom>
          {isEdit ? 'Edit Your Project' : 'Submit Your App'}
        </Typography>
        <Typography
          variant="h6"
          color="text.secondary"
          sx={{ maxWidth: 600, mx: 'auto' }}
        >
          {isEdit
            ? 'Update your project details and media.'
            : `Showcase your creation to the ${brand.productName} community.`}
        </Typography>
      </Box>

      <Grid container spacing={4}>
        {/* Form */}
        <Grid item xs={12} md={7}>
          <Card
            sx={{
              p: { xs: 3, md: 4 },
              background:
                theme.palette.mode === 'dark'
                  ? 'linear-gradient(135deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.02) 100%)'
                  : undefined,
              backdropFilter: 'blur(20px)',
              border: '1px solid',
              borderColor:
                theme.palette.mode === 'dark'
                  ? 'rgba(255,255,255,0.08)'
                  : 'rgba(0,0,0,0.06)',
              borderRadius: 3,
            }}
          >
            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            {/* Title */}
            <TextField
              fullWidth
              label="Project Title *"
              placeholder="My Amazing App"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              sx={{
                mb: 3,
                '& .MuiOutlinedInput-root': { borderRadius: 2 },
              }}
            />

            {/* Description */}
            <TextField
              fullWidth
              multiline
              rows={6}
              label="Description *"
              placeholder={`Describe your project, what it does, and how it was built with ${brand.productName}...`}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              sx={{
                mb: 3,
                '& .MuiOutlinedInput-root': { borderRadius: 2 },
              }}
            />

            {/* Category */}
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Category *</InputLabel>
              <Select
                value={category}
                label="Category *"
                onChange={(e) => setCategory(e.target.value)}
                sx={{ borderRadius: 2 }}
              >
                {categories.map((cat) => (
                  <MenuItem key={cat.slug} value={cat.slug}>
                    {cat.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Tags */}
            <Box sx={{ mb: 3 }}>
              <TextField
                fullWidth
                size="small"
                label="Tags"
                placeholder="Type a tag and press Enter"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={handleTagKeyDown}
                InputProps={{
                  endAdornment: (
                    <Button
                      size="small"
                      onClick={handleAddTag}
                      disabled={!tagInput.trim()}
                      sx={{ minWidth: 'auto', color: brand.colors.fire }}
                    >
                      <AddIcon fontSize="small" />
                    </Button>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': { borderRadius: 2 },
                }}
              />
              {tags.length > 0 && (
                <Box
                  sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 1 }}
                >
                  {tags.map((tag) => (
                    <Chip
                      key={tag}
                      label={tag}
                      size="small"
                      onDelete={() => handleRemoveTag(tag)}
                      sx={{
                        bgcolor: `rgba(${brand.colorsRgb.fire}, 0.1)`,
                        color: brand.colors.fire,
                        '& .MuiChip-deleteIcon': {
                          color: `rgba(${brand.colorsRgb.fire}, 0.5)`,
                          '&:hover': { color: brand.colors.fire },
                        },
                      }}
                    />
                  ))}
                </Box>
              )}
              <Typography variant="caption" color="text.disabled" sx={{ mt: 0.5, display: 'block' }}>
                Up to 10 tags. Press Enter or comma to add.
              </Typography>
              {tags.length < 10 && (
                <Box sx={{ mt: 1.5 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                    Suggested:
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {SUGGESTED_TAGS.filter((t) => !tags.includes(t)).map((tag) => (
                      <Chip
                        key={tag}
                        label={tag}
                        size="small"
                        variant="outlined"
                        onClick={() => {
                          if (tags.length < 10) setTags((prev) => [...prev, tag]);
                        }}
                        sx={{
                          cursor: 'pointer',
                          borderColor: 'divider',
                          '&:hover': {
                            bgcolor: `rgba(${brand.colorsRgb.fire}, 0.08)`,
                            borderColor: brand.colors.fire,
                          },
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              )}
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* URLs */}
            <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
              Links
            </Typography>
            <TextField
              fullWidth
              size="small"
              label="Live Instance URL"
              placeholder="https://my-app.example.com"
              value={instanceUrl}
              onChange={(e) => setInstanceUrl(e.target.value)}
              sx={{
                mb: 2,
                '& .MuiOutlinedInput-root': { borderRadius: 2 },
              }}
            />
            <TextField
              fullWidth
              size="small"
              label="Repository URL"
              placeholder="https://github.com/user/repo"
              value={repositoryUrl}
              onChange={(e) => setRepositoryUrl(e.target.value)}
              sx={{
                mb: 3,
                '& .MuiOutlinedInput-root': { borderRadius: 2 },
              }}
            />

            <Divider sx={{ my: 3 }} />

            {/* Media uploader */}
            <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
              Media
            </Typography>
            <MediaUploader
              files={files}
              onChange={setFiles}
              uploading={uploading}
              uploadProgress={uploadProgress}
            />

            {/* Submit */}
            <Button
              variant="contained"
              size="large"
              fullWidth
              onClick={handleSubmit}
              disabled={loading || uploading}
              startIcon={
                loading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <SaveIcon />
                )
              }
              sx={{
                mt: 4,
                py: 1.5,
                fontWeight: 700,
                fontSize: '1.05rem',
                borderRadius: 2,
                background: brand.gradients.button,
                color: brand.buttonTextColor,
                '&:hover': {
                  background: brand.gradients.buttonHover,
                },
                '&.Mui-disabled': {
                  background: 'action.disabledBackground',
                },
              }}
            >
              {loading
                ? 'Saving...'
                : isEdit
                ? 'Update Project'
                : 'Submit Project'}
            </Button>
          </Card>
        </Grid>

        {/* Preview panel */}
        <Grid item xs={12} md={5}>
          <Box sx={{ position: 'sticky', top: 100 }}>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
              Preview
            </Typography>
            <Card
              sx={{
                overflow: 'hidden',
                background:
                  theme.palette.mode === 'dark'
                    ? 'linear-gradient(135deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.02) 100%)'
                    : undefined,
                backdropFilter: 'blur(20px)',
                border: '1px solid',
                borderColor:
                  theme.palette.mode === 'dark'
                    ? 'rgba(255,255,255,0.08)'
                    : 'rgba(0,0,0,0.06)',
                borderRadius: 3,
              }}
            >
              {/* Thumbnail preview */}
              <Box
                sx={{
                  height: 180,
                  bgcolor:
                    theme.palette.mode === 'dark'
                      ? 'rgba(255,255,255,0.03)'
                      : 'rgba(0,0,0,0.03)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'hidden',
                }}
              >
                {files.length > 0 && files[0].type.startsWith('image/') ? (
                  <Box
                    component="img"
                    src={URL.createObjectURL(files[0])}
                    alt="Preview"
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                  />
                ) : (
                  <Typography variant="body2" color="text.disabled">
                    No thumbnail
                  </Typography>
                )}
              </Box>

              <Box sx={{ p: 2.5 }}>
                <Typography variant="h6" fontWeight={700} gutterBottom noWrap>
                  {title || 'Project Title'}
                </Typography>
                {category && (
                  <Chip
                    label={category}
                    size="small"
                    sx={{
                      mb: 1,
                      bgcolor: `rgba(${brand.colorsRgb.fire}, 0.12)`,
                      color: brand.colors.fire,
                      fontWeight: 600,
                      fontSize: '0.7rem',
                    }}
                  />
                )}
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    display: '-webkit-box',
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    mb: 1,
                  }}
                >
                  {description || 'Your project description will appear here...'}
                </Typography>
                {tags.length > 0 && (
                  <Box
                    sx={{
                      display: 'flex',
                      gap: 0.5,
                      flexWrap: 'wrap',
                      mt: 1,
                    }}
                  >
                    {tags.slice(0, 4).map((tag) => (
                      <Chip
                        key={tag}
                        label={tag}
                        size="small"
                        variant="outlined"
                        sx={{ height: 20, fontSize: '0.6rem' }}
                      />
                    ))}
                  </Box>
                )}
              </Box>
            </Card>
          </Box>
        </Grid>
      </Grid>
    </Container>
  );
};

export default GallerySubmitForm;
